'use client';

import { Globe } from 'lucide-react';
import { useLocale } from 'next-intl';
import { usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { useEffect, useRef, useState } from 'react';

interface LanguageSwitcherProps {
  className?: string;
  variant?: 'inline' | 'icon';
}

const localeLabels: Record<string, string> = {
  en: 'EN',
  'zh-TW': '中文',
  ja: '日本語',
  fr: 'FR',
};

export default function LanguageSwitcher({
  className = '',
  variant = 'inline',
}: LanguageSwitcherProps) {
  const locale = useLocale();
  const pathname = usePathname();
  const [isPending, setIsPending] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (variant !== 'icon' || !isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, variant]);

  const handleLocaleChange = (newLocale: string) => {
    if (newLocale === locale) return;
    setIsPending(true);
    setIsOpen(false);

    // Delete any stale NEXT_LOCALE cookies that may exist under
    // locale-prefixed paths (e.g. Path=/zh-TW or Path=/zh-TW/).
    // Without this, the browser can send TWO NEXT_LOCALE cookies and
    // the middleware reads the wrong one, trapping the user in a locale.
    // Also clear the root-path cookie first so we get a clean slate.
    document.cookie = `NEXT_LOCALE=;path=/;max-age=0`;
    for (const loc of routing.locales) {
      if (loc !== routing.defaultLocale) {
        document.cookie = `NEXT_LOCALE=;path=/${loc};max-age=0`;
        document.cookie = `NEXT_LOCALE=;path=/${loc}/;max-age=0`;
      }
    }

    // Set cookie so middleware respects user choice over Accept-Language header
    // Secure only over https (ZAP 10011); localhost dev would drop a Secure cookie.
    const secure = window.location.protocol === 'https:' ? ';Secure' : '';
    document.cookie = `NEXT_LOCALE=${newLocale};path=/;max-age=31536000;SameSite=Lax${secure}`;

    // Full page navigation for all locale switches to ensure cookie is
    // read by middleware on the next request. Using router.replace can cause
    // RSC fetch issues with localePrefix: 'as-needed'.
    const targetPath = newLocale === routing.defaultLocale
      ? (pathname === '/' ? '/' : pathname)
      : `/${newLocale}${pathname === '/' ? '' : pathname}`;
    window.location.href = targetPath;
  };

  if (variant === 'icon') {
    return (
      <div ref={menuRef} className={`relative ${className}`}>
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Change language"
          aria-haspopup="menu"
          aria-expanded={isOpen}
          className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] border border-border/60 bg-background text-[#383838] transition-all duration-200 hover:border-primary/30 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Globe className="h-4 w-4" />
        </button>

        {isOpen && (
          <div className="absolute top-full right-0 z-[70] mt-3 min-w-[140px] overflow-hidden rounded-2xl border border-border bg-background shadow-elevated-lg">
            <div className="p-2">
              {routing.locales.map((loc) => (
                <button
                  key={loc}
                  onClick={() => handleLocaleChange(loc)}
                  disabled={isPending}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left font-body text-sm transition-all duration-200 ${
                    locale === loc
                      ? 'bg-primary/10 text-primary'
                      : 'text-foreground hover:bg-secondary/60'
                  } ${isPending ? 'opacity-50' : ''}`}
                  aria-label={`Switch to ${localeLabels[loc]}`}
                >
                  <span>{localeLabels[loc]}</span>
                  {locale === loc && <span className="text-xs">•</span>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {routing.locales.map((loc, index) => (
        <span key={loc} className="flex items-center">
          {index > 0 && (
            <span className="text-border mx-1">/</span>
          )}
          <button
            onClick={() => handleLocaleChange(loc)}
            disabled={isPending}
            className={`text-sm font-medium transition-all duration-200 px-1.5 py-0.5 rounded ${
              locale === loc
                ? 'text-primary bg-primary/10'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            } ${isPending ? 'opacity-50' : ''}`}
            aria-label={`Switch to ${localeLabels[loc]}`}
          >
            {localeLabels[loc]}
          </button>
        </span>
      ))}
    </div>
  );
}
